import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPrice } from '@/utils/currency';
import { getProductThumbnail, getProductSlideshowImages } from '@/utils/productThumbnails';
import { ShippingInfo } from '@/components/ui';
import type { Product } from '@/types';
import styles from './ProductCard.module.css';

export interface ProductCardProps {
  product: Product;
  isSelected?: boolean;
  onSelect: (productId: string) => void;
}

const SLIDE_INTERVAL_MS = 1200;

export function ProductCard({ product, isSelected = false, onSelect }: ProductCardProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const slideshowImages = getProductSlideshowImages(product.id);
  const defaultThumb = getProductThumbnail(product.id);

  const clearSlideInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (slideshowImages.length === 0) return;
    setIsHovering(true);
    setSlideIndex(0);
  }, [slideshowImages.length]);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setSlideIndex(0);
    clearSlideInterval();
  }, [clearSlideInterval]);

  useEffect(() => {
    if (!isHovering || slideshowImages.length === 0) return;

    intervalRef.current = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slideshowImages.length);
    }, SLIDE_INTERVAL_MS);

    return clearSlideInterval;
  }, [isHovering, slideshowImages.length, clearSlideInterval]);

  const handleClick = useCallback(() => {
    onSelect(product.id);
  }, [onSelect, product.id]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(product.id);
    }
  };

  const currentImage =
    isHovering && slideshowImages.length > 0 ? slideshowImages[slideIndex] : defaultThumb;

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      <div className={styles.imageWrapper}>
        <img
          src={currentImage}
          alt={product.name}
          className={`${styles.image} ${isHovering ? styles.slideshowActive : ''}`}
          loading="lazy"
        />
        {isHovering && <span className={styles.aiBubble}>Examples of AI Generated Designs</span>}
        {isHovering && slideshowImages.length > 1 && (
          <div className={styles.slideIndicators}>
            {slideshowImages.map((_, i) => (
              <span
                key={i}
                className={`${styles.dot} ${i === slideIndex ? styles.dotActive : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className={styles.content}>
        <span className={styles.name}>{product.name}</span>
        {product.description && <p className={styles.description}>{product.description}</p>}
        <span className={styles.price}>{formatPrice(product.price)}</span>
        <span className={styles.priceNote}>shipping included</span>
        {product.shippingInfo && (
          <ShippingInfo
            turnaroundDays={product.shippingInfo.turnaroundDays}
            shippingDays={product.shippingInfo.shippingDays}
            variant="inline"
          />
        )}
      </div>
    </div>
  );
}
